import Icon from "components/Icons/Icon";
import { sizedPhoto, srcSetFor } from "utils/photoUrl";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { t } from "i18n/index";
import { discoverPath } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import React from "react";
import { getHomeTranslations } from "../marketing/i18n/pages/home";
import { ScrollArrows, useScrollRow } from "./ScrollRow";

// Card slots measure 208 CSS px on the live homepage (123yb6937gx). The stored URLs arrive at
// w=1080, so every tile was ~212 KB of JPEG for a 208 px box; asking the CDN for the real size
// and WebP takes the same image to ~42 KB.
const CARD_W = 208;

const ux = (id: string, w: number, h: number) => `https://images.unsplash.com/photo-${id}?fit=crop&auto=format&q=80&w=${w}&h=${h}`;

const slugify = (name: string) => name.toLowerCase().replace(/\s+/g, "-");

const COUNTRIES = [
	{ name: "Japan", flag: "jp", id: "1478436127897-769e1b3f0f36" },
	{ name: "Portugal", flag: "pt", id: "1513735492246-483525079686" },
	{ name: "Italy", flag: "it", id: "1516483638261-f4dbaf036963" },
	{ name: "Thailand", flag: "th", id: "1552465011-b4e21bf6e79a" },
	{ name: "Norway", flag: "no", id: "1531366936337-7c912a4589a7" },
	{ name: "Vietnam", flag: "vn", id: "1574699404005-b6120622b1e7" },
	{ name: "Morocco", flag: "ma", id: "1539020140153-e479b8c22e70" },
	{ name: "Indonesia", flag: "id", id: "1537996194471-e657df975ab4" },
	{ name: "Greece", flag: "gr", id: "1533105079780-92b9be482077" },
	{ name: "Spain", flag: "es", id: "1543783207-ec64e4d95325" },
	{ name: "France", flag: "fr", id: "1502602898657-3e91760cbb34" },
	{ name: "India", flag: "in", id: "1524492412937-b28074a5d7da" },
];

const COUNTRY_TILTS = [-3, 2.5, -2, 3.5, -2.5, 2, -3.5, 2, -2, 3, -2.5, 3];
// The card grid used to carry `rotate` per entry. It is presentation, not curation — the pick
// file has no business holding a tilt angle — so it is derived by position now. Same values,
// same order, so the grid looks identical on the fallback path.
const DEST_TILTS = [-2, 1.5, -1, 2, -1.5, 1.5, -2, 1, -1.5, 2];

// `country` is set only for city/region-level entries — it resolves the link to
// /discover/:country/:destination. Entries without it (Vietnam, Norway, ...) are
// themselves countries and link to /discover/:country instead.
//
// `slug` overrides the one derived from `name`, and exists because deriving it is wrong
// whenever the display name is a variant spelling or an abbreviation. Both cases were live:
// "Marrakech" gave `marrakech` where the destination is `marrakesh`, and "Lofoten" gave
// `lofoten` where it is `lofoten-islands` — two dead links on the homepage to pages that
// exist and are published. Same trap DestinationCardCompact documents in discoverSections.tsx.
// If you add an entry here, check the slug against explore_destinations rather than guessing.
const DESTINATIONS = [
	{ name: "Vietnam", flag: "vn", tag: "Coast & karst", id: "1574699404005-b6120622b1e7", rotate: -2 },
	{ name: "Lisbon", country: "Portugal", flag: "pt", tag: "City & sea", id: "1697748525265-7431cba075b6", rotate: 1.5 },
	{ name: "Kyoto", country: "Japan", flag: "jp", tag: "Temples & autumn", id: "1578469645742-46cae010e5d4", rotate: -1 },
	{ name: "Bali", country: "Indonesia", flag: "id", tag: "Rice & reefs", id: "1555400038-63f5ba517a47", rotate: 2 },
	{ name: "Marrakech", slug: "marrakesh", country: "Morocco", flag: "ma", tag: "Souks & sand", id: "1596750320291-a082a23dcc19", rotate: -1.5 },
	{ name: "Norway", flag: "no", tag: "Fjords & lights", id: "1485903266100-192c2e0af64f", rotate: 1.5 },
	{ name: "Thailand", flag: "th", tag: "Beaches & temples", id: "1552465011-b4e21bf6e79a", rotate: -2 },
	{ name: "Morocco", flag: "ma", tag: "Desert & medinas", id: "1489749798305-4fea3ae63d43", rotate: 1 },
	{ name: "Porto", country: "Portugal", flag: "pt", tag: "Wine & hills", id: "1513735492246-483525079686", rotate: -1.5 },
	{ name: "Lofoten", slug: "lofoten-islands", country: "Norway", flag: "no", tag: "Midnight sun", id: "1531366936337-7c912a4589a7", rotate: 2 },
];

const destinationHref = (d: { name: string; slug?: string; country?: string }) =>
	d.country ? `/discover/${slugify(d.country)}/${d.slug || slugify(d.name)}` : `/discover/${d.slug || slugify(d.name)}`;

// The geo these two rows link to, in the "thailand" / "thailand/bangkok" form loadReadyGeos()
// returns.
const destinationGeo = (d: { name: string; slug?: string; country?: string }) =>
	d.country ? `${slugify(d.country)}/${d.slug || slugify(d.name)}` : d.slug || slugify(d.name);

// Both rows are curated by hand, so they cannot be queried for. They CAN be edited without a
// deploy: the pick lives in `stippl-knowledge/copy/_homepage.md`, which Ilse owns, and arrives
// here as `picks` via the readyGeos loader. The arrays above are the fallback for when that
// document is missing — a stale row beats an empty one, and the homepage must render whatever
// the database is doing.
//
// The `liveOnly` filter stays either way. On 2026-08-28, **11 of the 22 hardcoded entries
// pointed at a page that does not exist** (Portugal, Norway, Morocco, Greece, India, Lisbon,
// Marrakech, Porto, Lofoten), and each took a visitor through the SPA catch-all to the login
// screen. The pick file drops those countries deliberately, and its build step refuses a geo
// with no facts file — but a geo can still be unpublished after a merge, so the runtime filter
// is the backstop for the gap between the two.
//
// Filtering rather than pruning means an entry lights up on its own the day its geo goes live.
// When the ready set is unknown — no loader data, e.g. a client-side navigation — everything
// renders, because hiding the whole section on a missing prop is worse than the dead links were.
const liveOnly = <T extends { geo: string }>(items: T[], ready?: Set<string>) => (ready ? items.filter((i) => ready.has(i.geo)) : items);

export interface HomepagePick {
	geo: string;
	label: string;
	href: string;
	photo: string;
	flag?: string;
	tagline?: string;
}

export interface HomepagePicks {
	trending?: HomepagePick[];
	popular?: HomepagePick[];
	/** The "Guides & inspiration" row, rendered by LatestArticlesSection. Articles, not geos. */
	journal?: { slug: string; title: string; href: string; photo: string; category?: string }[];
}

/** A row item, however it was sourced: from the pick file or from the arrays above. */
interface Card {
	name: string;
	href: string;
	photo: string;
	flag?: string;
	tag?: string;
	geo: string;
}

const fromPick = (p: HomepagePick): Card => ({
	name: p.label,
	// `href` comes from `geo`, which is why the pick file fixes the slug-guessing bug the
	// DESTINATIONS comment documents: `marrakech` for `marrakesh`, `lofoten` for
	// `lofoten-islands`. A geo that must resolve to a facts file cannot be wrong that way.
	href: p.href,
	photo: p.photo,
	flag: p.flag,
	tag: p.tagline,
	geo: p.geo,
});

const fromCountry = (c: (typeof COUNTRIES)[number]): Card => ({
	name: c.name,
	href: `/discover/${slugify(c.name)}`,
	photo: ux(c.id, 200, 200),
	flag: c.flag,
	geo: slugify(c.name),
});

const fromDestination = (d: (typeof DESTINATIONS)[number]): Card => ({
	name: d.name,
	href: destinationHref(d),
	photo: ux(d.id, 500, 420),
	flag: d.flag,
	tag: d.tag,
	geo: destinationGeo(d),
});

const TrendingDestinationsSection = ({ readyGeos, picks }: { readyGeos?: { geo: string }[]; picks?: HomepagePicks }) => {
	const ready = React.useMemo(() => (readyGeos ? new Set(readyGeos.map((r) => r.geo)) : undefined), [readyGeos]);
	const countries = liveOnly(picks?.trending?.length ? picks.trending.map(fromPick) : COUNTRIES.map(fromCountry), ready);
	const destinations = liveOnly(picks?.popular?.length ? picks.popular.map(fromPick) : DESTINATIONS.map(fromDestination), ready);
	const countryRow = useScrollRow<HTMLDivElement>();
	const destRow = useScrollRow<HTMLDivElement>();
	const lang = useMarketingLang();
	const translations = getHomeTranslations(lang);
	// Both "browse all" CTAs below used to be <a href="/discover">, the dead browse index. They
	// are buttons now for the reason CommunitySection spells out: an href survives middle-click
	// and "open in new tab", and neither /discover nor /register is worth landing on. The
	// country and destination cards stay real anchors — they point at published guide pages.
	const gate = usePlanGate("homepage_trending", "store");

	return (
		<section className="trending-destinations">
			<div className="trending-destinations__inner">
				<div className="trending-destinations__header">
					<div className="trending-destinations__intro">
						<p className="sub-header">{t(translations, "home.trending.subHeader", "Trending now")}</p>
						<h2>
							{t(translations, "home.trending.headingLine1", "Don't just scroll.")}
							<br />
							<span>{t(translations, "home.trending.headingAccent", "Pick your next adventure.")}</span>
						</h2>
						<p className="trending-destinations__desc">
							{t(translations, "home.trending.description", "Over 2 million trips planned on Stippl, here's where travelers are heading right now.")}
						</p>
					</div>
					<div className="trending-destinations__header-actions">
						<ScrollArrows {...countryRow.arrowProps} ariaLabel="Trending countries" />
						<button type="button" className="trending-destinations__browse-all" onClick={gate.requestPlan}>
							{t(translations, "home.trending.browseAll", "Browse all")}
							<Icon name="chevron-right" size={8} fill="#0f3e4a" />
						</button>
					</div>
				</div>

				<div className="trending-destinations__countries" ref={countryRow.ref}>
					{countries.map((c, i) => (
						<a
							// Wrapped here rather than in `fromCountry`/`fromPick`: `c.href` can come from
							// either the hardcoded fallback array or the KB-curated pick file (see the note
							// on `HomepagePicks` above), and the pick file's `href` is a bare `/discover/...`
							// string with no locale awareness of its own. Fixing it at the one place both
							// sources funnel through covers both instead of only the hardcoded half.
							href={discoverPath(c.href, lang)}
							className="country-card"
							key={c.geo}
							style={{ ["--card-rotate" as string]: `${COUNTRY_TILTS[i % COUNTRY_TILTS.length]}deg` } as React.CSSProperties}
						>
							<div className="country-card__photo">
								<div className="country-card__circle">
									<img src={sizedPhoto(c.photo, CARD_W)} srcSet={srcSetFor(c.photo, CARD_W) || undefined} alt={c.name} loading="lazy" />
								</div>
								<span className="country-card__flag">
									<img src={`https://flagcdn.com/w40/${c.flag}.png`} alt="" loading="lazy" />
								</span>
							</div>
							<p>{c.name}</p>
						</a>
					))}
				</div>

				<div className="trending-destinations__popular-header">
					<h3>{t(translations, "home.trending.popularHeading", "Popular right now")}</h3>
					<div className="trending-destinations__header-actions">
						<button type="button" className="trending-destinations__all-link" onClick={gate.requestPlan}>
							{t(translations, "home.trending.allDestinationsLink", "All destinations")}
							<Icon name="chevron-right" size={8} fill="#4a6e7a" />
						</button>
					</div>
				</div>

				<div className="trending-destinations__grid" ref={destRow.ref}>
					{destinations.map((d, i) => (
						<a
							href={discoverPath(d.href, lang)}
							className="dest-card"
							key={d.geo}
							style={{ ["--card-rotate" as string]: `${DEST_TILTS[i % DEST_TILTS.length]}deg` } as React.CSSProperties}
						>
							<img src={sizedPhoto(d.photo, CARD_W)} srcSet={srcSetFor(d.photo, CARD_W) || undefined} alt={d.name} loading="lazy" />
							<div className="dest-card__gradient" />
							<span className="dest-card__flag">
								<img src={`https://flagcdn.com/w40/${d.flag}.png`} alt="" loading="lazy" />
							</span>
							<div className="dest-card__text">
								<p className="dest-card__name">{d.name}</p>
								<p className="dest-card__tag">{d.tag}</p>
							</div>
						</a>
					))}
				</div>
			</div>
			<PlanGateModal
				gate={gate}
				title={t(translations, "home.auth.destinations", "Create a free account to browse every destination")}
				loginTitle={t(translations, "home.auth.login", "Log in to plan with AI")}
			/>
		</section>
	);
};

export default TrendingDestinationsSection;
